import { DataTable } from '@/components/ui/DataTable'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useCurrentProject } from '../useCurrentProject'

type DatabricksJobRun = RestSchemas['DatabricksMessages.DatabricksJobRun']

export function ProjectDatabricksRunsSection() {
  const { projectId } = useCurrentProject()
  const { getDatabricksApiListProjectJobRuns, postDatabricksApiRefreshProjectRunStatus } = useRestClient()

  const { data: runs, isLoading } = getDatabricksApiListProjectJobRuns.useQuery({
    path: { projectId },
  })
  const { mutate: refreshRunStatus } = postDatabricksApiRefreshProjectRunStatus.useMutation()
  const invalidateRuns = () => getDatabricksApiListProjectJobRuns.invalidateQueries()

  const columns: ColumnDef<DatabricksJobRun>[] = [
    {
      id: 'runId',
      accessorKey: 'runId',
      header: ({ column }) => <SortingHeaderButton column={column}>Run ID</SortingHeaderButton>,
      cell: ({ row }) => (
        <div className="flex flex-col">
          {row.original.runUrl ? (
            <a href={row.original.runUrl} target="_blank" rel="nofollow">{row.original.runId}</a>
          ) : (
            <div>{row.original.runId}</div>
          )}
          <span className="text-xs text-muted-foreground">{row.original.jobName}</span>
        </div>
      ),
    },
    {
      id: 'startTime',
      accessorKey: 'startTime',
      header: ({ column }) => <SortingHeaderButton column={column}>Start - End</SortingHeaderButton>,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span><span className="font-bold">Start - </span>{row.original.startTime ? format(row.original.startTime, 'PP (p)') : 'N/A'}</span>
          <span><span className="font-bold">End - </span>{row.original.endTime ? format(row.original.endTime, 'PP (p)') : 'N/A'}</span>
        </div>
      ),
    },
    {
      id: 'state',
      accessorKey: 'state',
      header: 'State',
      cell: ({ row }) => <Badge variant="outline">{row.original.state}</Badge>,
    },
    {
      id: 'result',
      accessorKey: 'result',
      header: 'Result',
      cell: ({ row }) => {
        const variant = row.original.result === 'SUCCESS' ? 'success' : row.original.result === 'FAILED' ? 'error' : 'default'
        return <Badge variant={variant}>{row.original.result}</Badge>
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="size-8 rounded-full">
              <IconMenuOutline size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => {
              refreshRunStatus(
                { path: { runId: row.original.runId! }, query: { projectId } },
                {
                  onError: (e) => toast.error('Error refreshing run: ' + getDatabricksErrorMessage(e)),
                  onSettled: () => invalidateRuns(),
                }
              )
            }}>Refresh</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => {
              if (row.original.logs) {
                const blob = new Blob([row.original.logs], { type: 'text/plain' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `logs-${row.original.jobName}-${row.original.runId}.txt`
                a.click()
                URL.revokeObjectURL(url)
              } else {
                toast.error('No logs available')
              }
            }}>Download Logs</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading runs...</div>
  }

  return <DataTable title="Runs" columns={columns} data={runs ?? []} />
}
